import { Activity } from "lucide-react";
import { bmiCategory, computeMetrics, whtrCategory } from "@/lib/bodyMetrics";

// Distance outside the healthy range (0 = healthy).
const bmiGap = (v) => (v == null ? null : v < 18.5 ? 18.5 - v : v >= 25 ? v - 24.9 : 0);
const whtrGap = (v) => (v == null ? null : v >= 0.5 ? v - 0.49 : 0);

const METRICS = [
  { key: "weight_kg", label: "Weight", unit: "kg", dp: 1, gap: (v, h) => bmiGap(computeMetrics(v, h, null).bmi) },
  { key: "waist_cm", label: "Waist", unit: "cm", dp: 1, gap: (v, h) => whtrGap(computeMetrics(null, h, v).whtr) },
  { key: "bmi", label: "BMI", unit: "", dp: 1, gap: (v) => bmiGap(v), cat: bmiCategory },
  { key: "whtr", label: "Waist-to-height", unit: "", dp: 2, gap: (v) => whtrGap(v), cat: whtrCategory },
];

const TONE = {
  good: "border-emerald-300 bg-emerald-50 text-emerald-800",
  bad: "border-amber-300 bg-amber-50 text-amber-900",
  neutral: "border-zinc-200 bg-zinc-50 text-zinc-600",
};

function progress(metric, points) {
  const withVal = points.filter((p) => p[metric.key] != null);
  if (withVal.length === 0) return null;
  const first = withVal[0], last = withVal[withVal.length - 1];
  const height = [...points].reverse().find((p) => p.height_cm)?.height_cm;
  const delta = last[metric.key] - first[metric.key];
  const pct = first[metric.key] ? (delta / first[metric.key]) * 100 : 0;
  let tone = "neutral";
  if (withVal.length > 1 && Math.abs(pct) >= 1) {
    const g0 = metric.gap(first[metric.key], height), g1 = metric.gap(last[metric.key], height);
    if (g0 != null && g1 != null && g1 < g0) tone = "good";
    else if (g0 != null && g1 != null && g1 > g0) tone = "bad";
  }
  const c0 = metric.cat?.(first[metric.key]), c1 = metric.cat?.(last[metric.key]);
  return { first, last, delta, pct, tone, count: withVal.length, rangeChange: c0 && c1 && c0.label !== c1.label ? `${c0.label} → ${c1.label}` : null, cat: c1 };
}

const fmt = (v, dp) => Number(v).toFixed(dp);
const signed = (v, dp) => `${v > 0 ? "+" : v < 0 ? "−" : "±"}${Math.abs(v).toFixed(dp)}`;

function MetricTile({ metric, p }) {
  const unit = metric.unit ? ` ${metric.unit}` : "";
  return (
    <div className={`rounded-md border p-3 ${TONE[p.tone]}`} data-testid={`progress-${metric.key}`} data-tone={p.tone}>
      <div className="text-2xs uppercase tracking-wider opacity-80">{metric.label}</div>
      {p.count > 1 ? (
        <>
          <div className="text-lg font-semibold font-mono-num leading-tight mt-0.5" data-testid={`progress-${metric.key}-delta`}>
            {signed(p.delta, metric.dp)}{unit}
            <span className="text-xs font-normal ml-1.5">· {signed(p.pct, 1)}%</span>
          </div>
          <div className="text-xs mt-0.5 font-mono-num" data-testid={`progress-${metric.key}-range`}>
            {fmt(p.first[metric.key], metric.dp)} → {fmt(p.last[metric.key], metric.dp)}{unit}
          </div>
        </>
      ) : (
        <div className="text-lg font-semibold font-mono-num leading-tight mt-0.5" data-testid={`progress-${metric.key}-delta`}>
          {fmt(p.last[metric.key], metric.dp)}{unit}
        </div>
      )}
      {p.rangeChange ? (
        <div className="text-2xs font-medium mt-1" data-testid={`progress-${metric.key}-category`}>{p.rangeChange}</div>
      ) : p.cat ? (
        <div className="text-2xs mt-1 opacity-80" data-testid={`progress-${metric.key}-category`}>{p.cat.label}</div>
      ) : null}
      <div className="text-2xs mt-1 opacity-70">
        {p.count > 1
          ? `since ${new Date(p.first.date).toLocaleDateString()} · ${p.count} assessments`
          : "Only one measurement so far"}
      </div>
    </div>
  );
}

export function MeasurementProgressCard({ points }) {
  const tiles = METRICS.map((m) => ({ m, p: progress(m, points || []) })).filter((t) => t.p);
  if (tiles.length === 0) return null;
  return (
    <div className="rounded border border-zinc-200 p-3 space-y-2" data-testid="measurement-progress-card">
      <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <Activity className="h-3.5 w-3.5" /> Progress since first measured
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {tiles.map(({ m, p }) => <MetricTile key={m.key} metric={m} p={p} />)}
      </div>
    </div>
  );
}
